import React, { useContext, useEffect, useState, useRef } from 'react';
import moment, { type Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { Button, Card, Popup, Table } from '_atoms';
import { Context } from '_templates/AnalyticsDashboard/analytics-dashboard';
import { analyticWidgets, type AnalyticsFilterBody } from 'actions/analyticActions';
import { openingMap } from '_templates/utils';
import { ReactComponent as Upload } from 'dist/icons/upload.svg';
import { ReactComponent as Empty } from 'dist/images/common/icons/changelog.svg';
import { columns, COLUMNS_BE } from '../AnalyticsDashboard/OpeningsTable';
import type {
  OpeningTableKey,
  OpeningTableOptions,
  OpeningTablesData,
  OpeningTablesLoading,
  OpeningTablesOptions,
  OpeningsWidgetResponse,
} from './types';

export const INITIAL_VALUES: OpeningTableOptions = {
  sorting: { key: 'job_name', order: 'asc', field: 'Job name' },
  limit: { value: 30, label: 30 },
  page: 1,
  offset: 0,
  itemsCount: 0,
};

const DEFAULT_SHOWN_COLUMNS: Record<string, boolean> = {
  priority: true,
  job_name: true,
  team_lead: true,
  expected_date: true,
  technical_level: true,
  status: true,
};

const getInitialShownColumns = (): Record<string, boolean> => {
  const stored = sessionStorage.getItem('analyticsOpenings');
  return stored ? JSON.parse(stored) : DEFAULT_SHOWN_COLUMNS;
};

interface AnalyticsOpeningsEmptyProps {
  title?: string;
}

const AnalyticsOpeningsEmpty = ({ title }: AnalyticsOpeningsEmptyProps) => {
  const { t } = useTranslation();
  return (
    <Card className="analytics-openings__empty">
      {!!title && <h2>{title}</h2>}
      <Empty />
      <h3>{t('translation:analytics_opening_info.text_no_data_found')}</h3>
      <p>{t('translation:analytics_opening_info.text_try_another_criteria')}</p>
    </Card>
  );
};

export const AnalyticsOpeningInfo = () => {
  const { t } = useTranslation();
  const { appliedFilters } = useContext(Context)!;
  const [openings, setOpenings] = useState<OpeningTablesData>({
    active: [],
    expected: [],
    created: [],
    summary: [],
  });
  const [options, setOptions] = useState<OpeningTablesOptions>({
    active: INITIAL_VALUES,
    expected: INITIAL_VALUES,
    created: INITIAL_VALUES,
    summary: INITIAL_VALUES,
  });
  const [tableOpeningsLoading, setTableOpeningsLoading] = useState<OpeningTablesLoading>({});
  const [exportPopupOpen, setExportPopupOpen] = useState(false);
  const [successPopupOpen, setSuccessPopupOpen] = useState(false);
  const [shownColumns, setShownColumns] = useState<Record<string, boolean>>(getInitialShownColumns);

  const isFirstRender = useRef({ active: false, expected: true, created: true, summary: true });

  const isOpeningsLoading = Object.values(tableOpeningsLoading).some(Boolean);

  const updateOpeningsTableParams = (table: OpeningTableKey, params: Partial<OpeningTableOptions>) => {
    setOptions(p => ({ ...p, [table]: { ...p[table], ...params } }));
  };

  const exportExcel = () => {
    const periods: Array<{ period_filter: string; from_date: string; to_date: string }> = [];
    if (appliedFilters.expected?.length) {
      periods.push({
        period_filter: 'Expected',
        from_date: appliedFilters.expected[0].format('YYYY-MM-DD'),
        to_date: appliedFilters.expected[1].format('YYYY-MM-DD'),
      });
    }
    if (appliedFilters.active?.length) {
      periods.push({
        period_filter: 'Active',
        from_date: appliedFilters.active[0].format('YYYY-MM-DD'),
        to_date: appliedFilters.active[1].format('YYYY-MM-DD'),
      });
    }
    if (appliedFilters.created?.length) {
      periods.push({
        period_filter: 'Created at',
        from_date: appliedFilters.created[0].format('YYYY-MM-DD'),
        to_date: appliedFilters.created[1].format('YYYY-MM-DD'),
      });
    }
    analyticWidgets(
      'openings/export/xlsx',
      {
        ...appliedFilters,
        periods,
        sorting: [
          { field: options.expected.sorting.field, order: options.expected.sorting.order, period_filter: 'Expected' },
          { field: options.active.sorting.field, order: options.active.sorting.order, period_filter: 'Active' },
          { field: options.created.sorting.field, order: options.created.sorting.order, period_filter: 'Created at' },
        ],
        columns: Object.entries(shownColumns)
          .filter(([, value]) => !!value)
          .map(([key]) => COLUMNS_BE[key]),
      } as AnalyticsFilterBody,
      null
    ).then(() => {
      setSuccessPopupOpen(true);
      setExportPopupOpen(false);
    });
  };

  const getOpenings = (tablesToUpdate: Array<'active' | 'expected' | 'created'>, loadSummary?: boolean) => {
    const { active, expected, created } = appliedFilters;
    const filters = (({ period: _period, active: _a, expected: _e, created: _c, ...rest }) => rest)(appliedFilters);
    const minPeriod = [active?.[0], expected?.[0], created?.[0]].filter(Boolean) as Moment[];
    const maxPeriod = [active?.[1], expected?.[1], created?.[1]].filter(Boolean) as Moment[];
    tablesToUpdate.forEach(d => {
      if (!appliedFilters[d]?.length) {
        setOpenings(o => ({ ...o, [d]: [] }));
        return;
      }
      setTableOpeningsLoading(l => ({ ...l, [d]: true }));
      analyticWidgets(
        `openings/${d}`,
        {
          ...filters,
          period: [appliedFilters[d][0], appliedFilters[d][1]],
          sorting: [{ field: options[d].sorting.field, order: options[d].sorting.order }],
        } as AnalyticsFilterBody,
        null,
        `?limit=${options[d].limit.value}&offset=${(options[d].page - 1) * options[d].limit.value}`
      )
        .then((resp: OpeningsWidgetResponse) => {
          updateOpeningsTableParams(d, { itemsCount: resp.count, next: !!resp.next, prev: !!resp.previous });
          setOpenings(o => ({ ...o, [d]: openingMap(resp) }));
        })
        .finally(() => {
          setTableOpeningsLoading(l => ({ ...l, [d]: false }));
        });
    });

    if (loadSummary) {
      setTableOpeningsLoading(l => ({ ...l, summary: true }));
      const summeryPeriods: Array<{ period_filter: string; from_date: string; to_date: string }> = [];
      if (expected?.length) {
        summeryPeriods.push({
          period_filter: 'Expected',
          from_date: expected[0].format('YYYY-MM-DD'),
          to_date: expected[1].format('YYYY-MM-DD'),
        });
      }
      if (active?.length) {
        summeryPeriods.push({
          period_filter: 'Active',
          from_date: active[0].format('YYYY-MM-DD'),
          to_date: active[1].format('YYYY-MM-DD'),
        });
      }
      if (created?.length) {
        summeryPeriods.push({
          period_filter: 'Created at',
          from_date: created[0].format('YYYY-MM-DD'),
          to_date: created[1].format('YYYY-MM-DD'),
        });
      }
      analyticWidgets(
        `openings/summary`,
        {
          ...filters,
          period: [moment.min(minPeriod), moment.max(maxPeriod)],
          sorting: [{ field: options.summary.sorting.field, order: options.summary.sorting.order }],
          summary_periods: summeryPeriods,
        } as AnalyticsFilterBody,
        null,
        `?limit=${options.summary.limit.value}&offset=${(options.summary.page - 1) * options.summary.limit.value}`
      )
        .then((resp: OpeningsWidgetResponse) => {
          updateOpeningsTableParams('summary', { itemsCount: resp.count, next: !!resp.next, prev: !!resp.previous });
          setOpenings(o => ({ ...o, summary: openingMap(resp) }));
        })
        .finally(() => {
          setTableOpeningsLoading(l => ({ ...l, summary: false }));
        });
    }
  };

  useEffect(() => {
    if (isFirstRender.current.active) {
      isFirstRender.current.active = false;
    } else {
      getOpenings(['active']);
    }
  }, [options.active.offset, options.active.limit, options.active.sorting, options.active.page, appliedFilters]);

  useEffect(() => {
    if (isFirstRender.current.expected) {
      isFirstRender.current.expected = false;
    } else {
      getOpenings(['expected']);
    }
  }, [
    options.expected.offset,
    options.expected.limit,
    options.expected.sorting,
    options.expected.page,
    appliedFilters,
  ]);

  useEffect(() => {
    if (isFirstRender.current.created) {
      isFirstRender.current.created = false;
    } else {
      getOpenings(['created']);
    }
  }, [options.created.offset, options.created.limit, options.created.sorting, options.created.page, appliedFilters]);

  useEffect(() => {
    if (isFirstRender.current.summary) {
      isFirstRender.current.summary = false;
    } else {
      getOpenings([], true);
    }
  }, [options.summary.offset, options.summary.limit, options.summary.sorting, options.summary.page, appliedFilters]);

  if (!isOpeningsLoading && !openings.active.length && !openings.expected.length && !openings.created.length) {
    return <AnalyticsOpeningsEmpty />;
  }

  return (
    <div className="analytics-openings">
      {!!appliedFilters.active?.length &&
        (openings.active.length || tableOpeningsLoading.active ? (
          <Table
            rowKey="uuid"
            cardTitle={t('translation:analytics_opening_info.card_title_active')}
            cardCount={options.active.itemsCount}
            extra={
              <Button onClick={() => setExportPopupOpen(true)} icon={<Upload />} className="export-excel">
                {t('translation:analytics_opening_info.button_export_excel')}
              </Button>
            }
            columns={columns(options.active.sorting, sorting => updateOpeningsTableParams('active', { sorting }))}
            data={openings.active}
            limit={options.active.limit}
            page={options.active.page}
            updateParams={(key, val) =>
              setOptions(o => ({
                ...o,
                active: {
                  ...o.active,
                  [key]: val,
                  offset: key === 'page' ? ((val as number) - 1) * o.active.limit.value : o.active.offset,
                },
              }))
            }
            itemsCount={options.active.itemsCount}
            loading={tableOpeningsLoading.active}
            customizable
            tableName="analyticsOpenings"
            shownColumns={shownColumns}
            setShownColumns={setShownColumns}
          />
        ) : (
          <AnalyticsOpeningsEmpty title={t('translation:analytics_opening_info.card_title_active')} />
        ))}
      {!!appliedFilters.expected?.length &&
        (openings.expected.length ? (
          <Table
            rowKey="uuid"
            cardTitle={t('translation:analytics_opening_info.card_title_expected')}
            cardCount={options.expected.itemsCount}
            extra={
              !appliedFilters.active?.length &&
              !openings.active.length && (
                <Button onClick={() => setExportPopupOpen(true)} icon={<Upload />} className="export-excel">
                  {t('translation:analytics_opening_info.button_export_excel')}
                </Button>
              )
            }
            columns={columns(options.expected.sorting, sorting => updateOpeningsTableParams('expected', { sorting }))}
            data={openings.expected}
            limit={options.expected.limit}
            page={options.expected.page}
            updateParams={(key, val) =>
              setOptions(o => ({
                ...o,
                expected: {
                  ...o.expected,
                  [key]: val,
                  offset: key === 'page' ? ((val as number) - 1) * o.expected.limit.value : o.expected.offset,
                },
              }))
            }
            itemsCount={options.expected.itemsCount}
            loading={tableOpeningsLoading.expected}
            customizable={!appliedFilters.active?.length && !openings.active.length}
            tableName="analyticsOpenings"
            shownColumns={shownColumns}
            setShownColumns={setShownColumns}
          />
        ) : (
          <AnalyticsOpeningsEmpty title={t('translation:analytics_opening_info.card_title_expected')} />
        ))}
      {!!appliedFilters.created?.length &&
        (openings.created.length ? (
          <Table
            rowKey="uuid"
            cardTitle={t('translation:analytics_opening_info.card_title_created')}
            cardCount={options.created.itemsCount}
            extra={
              !appliedFilters.active?.length &&
              !openings.active.length &&
              !appliedFilters.expected?.length &&
              !openings.expected.length && (
                <Button onClick={() => setExportPopupOpen(true)} icon={<Upload />} className="export-excel">
                  {t('translation:analytics_opening_info.button_export_excel')}
                </Button>
              )
            }
            columns={columns(options.created.sorting, sorting => updateOpeningsTableParams('created', { sorting }))}
            data={openings.created}
            limit={options.created.limit}
            page={options.created.page}
            updateParams={(key, val) =>
              setOptions(o => ({
                ...o,
                created: {
                  ...o.created,
                  [key]: val,
                  offset: key === 'page' ? ((val as number) - 1) * o.created.limit.value : o.created.offset,
                },
              }))
            }
            itemsCount={options.created.itemsCount}
            loading={tableOpeningsLoading.created}
            customizable={
              !appliedFilters.active?.length &&
              !openings.active.length &&
              !appliedFilters.expected?.length &&
              !openings.expected.length
            }
            tableName="analyticsOpenings"
            shownColumns={shownColumns}
            setShownColumns={setShownColumns}
          />
        ) : (
          <AnalyticsOpeningsEmpty title={t('translation:analytics_opening_info.card_title_created')} />
        ))}
      {(appliedFilters.active?.length || 0) +
        (appliedFilters.expected?.length || 0) +
        (appliedFilters.created?.length || 0) >
        2 &&
        (openings.summary.length ? (
          <Table
            rowKey="uuid"
            cardTitle={t('translation:analytics_opening_info.card_title_summary')}
            cardCount={options.summary.itemsCount}
            columns={columns(options.summary.sorting, sorting => updateOpeningsTableParams('summary', { sorting }))}
            data={openings.summary}
            limit={options.summary.limit}
            page={options.summary.page}
            updateParams={(key, val) =>
              setOptions(o => ({
                ...o,
                summary: {
                  ...o.summary,
                  [key]: val,
                  offset: key === 'page' ? ((val as number) - 1) * o.summary.limit.value : o.summary.offset,
                },
              }))
            }
            itemsCount={options.summary.itemsCount}
            loading={tableOpeningsLoading.summary}
            tableName="analyticsOpenings"
            shownColumns={shownColumns}
          />
        ) : (
          <AnalyticsOpeningsEmpty title={t('translation:analytics_opening_info.card_title_summary')} />
        ))}
      <Popup
        open={exportPopupOpen}
        title={t('translation:analytics_opening_info.popup_title_export_excel')}
        cancelButtonProps={{
          onClick: () => setExportPopupOpen(false),
          children: t('translation:analytics_opening_info.button_go_back'),
        }}
        okButtonProps={{
          onClick: exportExcel,
          children: t('translation:analytics_opening_info.button_send_to_email'),
        }}
      >
        {t('translation:analytics_opening_info.text_export_excel')}
      </Popup>
      <Popup
        open={successPopupOpen}
        title={t('translation:analytics_opening_info.popup_title_success')}
        confirmTxt={t('translation:analytics_opening_info.button_ok')}
        okButtonProps={{
          onClick: () => setSuccessPopupOpen(false),
          children: t('translation:analytics_opening_info.button_ok'),
        }}
      >
        {t('translation:analytics_opening_info.text_file_sent')}
      </Popup>
    </div>
  );
};
